import * as THREE from 'three'
import { Texture, TextureLoader } from 'three'

const loader = new TextureLoader()
const sourceByUrl = new Map<string, Texture>()
const inflightByUrl = new Map<string, Promise<Texture>>()
const TEXTURE_TARGET_ANISOTROPY = 16

export function cleanTextureUrl(url: string | undefined | null): string | null {
  if (!url?.trim()) return null
  return url.split('?')[0]
}

export function applyTextureQuality(
  texture: Texture,
  options: { colorSpace?: THREE.ColorSpace } = {},
): void {
  const maxSupportedAnisotropy =
    Number((globalThis as { __maxTextureAnisotropy?: number }).__maxTextureAnisotropy) || 1
  const anisotropy = Math.max(1, Math.min(TEXTURE_TARGET_ANISOTROPY, maxSupportedAnisotropy))
  texture.colorSpace = options.colorSpace ?? THREE.SRGBColorSpace
  texture.minFilter = THREE.LinearMipmapLinearFilter
  texture.magFilter = THREE.LinearFilter
  texture.generateMipmaps = true
  texture.anisotropy = anisotropy
  texture.needsUpdate = true
}

export function refreshAllTextureAnisotropy(root?: THREE.Object3D): void {
  if (!root) return

  const maxSupportedAnisotropy =
    Number((globalThis as { __maxTextureAnisotropy?: number }).__maxTextureAnisotropy) || 1
  const anisotropy = Math.max(1, Math.min(TEXTURE_TARGET_ANISOTROPY, maxSupportedAnisotropy))

  root.traverse((obj) => {
    const mesh = obj as THREE.Mesh
    if (!mesh.isMesh || !mesh.material) return

    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
    for (const material of materials) {
      for (const key of ['map', 'normalMap', 'bumpMap', 'roughnessMap', 'metalnessMap'] as const) {
        const texture = material[key]
        if (texture?.isTexture) {
          if (key === 'map') {
            texture.colorSpace = THREE.SRGBColorSpace
          } else if (key === 'bumpMap' || key === 'normalMap' || key === 'roughnessMap' || key === 'metalnessMap') {
            texture.colorSpace = THREE.NoColorSpace
          }
          texture.anisotropy = anisotropy
          texture.minFilter = THREE.LinearMipmapLinearFilter
          texture.magFilter = THREE.LinearFilter
          texture.generateMipmaps = true
          texture.needsUpdate = true
        }
      }
    }
  })
}

export function rememberCachedTexture(cleanUrl: string, source: Texture): void {
  if (!cleanUrl || sourceByUrl.has(cleanUrl)) return
  sourceByUrl.set(cleanUrl, source)
}

export function cloneCachedTexture(cleanUrl: string): Texture | null {
  const source = sourceByUrl.get(cleanUrl)
  if (!source) return null
  const clone = source.clone()
  applyTextureQuality(clone)
  return clone
}

export function preloadTexture(url: string | undefined | null): Promise<Texture | null> {
  const cleanUrl = cleanTextureUrl(url)
  if (!cleanUrl) return Promise.resolve(null)

  const existing = sourceByUrl.get(cleanUrl)
  if (existing) return Promise.resolve(existing.clone())

  const inflight = inflightByUrl.get(cleanUrl)
  if (inflight) {
    return inflight.then((source) => source.clone())
  }

  const loadPromise = new Promise<Texture>((resolve, reject) => {
    loader.load(
      `${cleanUrl}?not-from-cache-please`,
      (texture) => {
        applyTextureQuality(texture)
        rememberCachedTexture(cleanUrl, texture)
        inflightByUrl.delete(cleanUrl)
        resolve(texture)
      },
      undefined,
      (error) => {
        inflightByUrl.delete(cleanUrl)
        reject(error)
      },
    )
  })

  inflightByUrl.set(cleanUrl, loadPromise)
  return loadPromise.then((source) => source.clone())
}

export async function preloadSceneTextureUrls(
  urls: Iterable<string | undefined | null>,
): Promise<void> {
  const unique = new Set<string>()
  for (const url of urls) {
    const cleanUrl = cleanTextureUrl(url)
    if (cleanUrl) unique.add(cleanUrl)
  }
  if (unique.size === 0) return
  await Promise.all([...unique].map((cleanUrl) => preloadTexture(cleanUrl)))
}
